import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import ChartShell from '@/components/Chart/ChartShell';
import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig,
  mergeChartEvents
} from '@/components/Chart/hooks/options/useChartFunctions';
import useRegularChart from '@/components/Chart/RegularChart/useRegularChart';
import type { ExtendedOptions, WatermarkAttributes } from '@/components/Chart/types/highcharts';
import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import TimeRangeButtons from '@/components/TimeRangeButtons/TimeRangeButtons';
import type { TimeRangeButton } from '@/constants/constants';
import {
  DEFAULT_CHART_HEIGHT,
  regularChartPalette,
  TABLE_ROW_HEIGHT_PX
} from '@/constants/constants';
import { chartOptions } from '@/features/Charts/api';
import useCsvDownload from '@/hooks/useCsvDownload';
import useUserDetails from '@/hooks/useUserDetails';
import { deepMerge } from '@/utils/deepMerge';
import { detectEnvironment } from '@/utils/detectEnvironment';

import type Highcharts from 'highcharts/highstock';

const environment = detectEnvironment();
const GROUP_NAME = `Meta_Go_Config${environment === 'staging' ? '_Staging' : ''}`;
const WATERMARK_NAME = '/watermark-black.svg';

type LayoutProps = {
  fullWidth?: boolean;
  height?: number;
};

type CustomChartProps = {
  metadata?: Record<string, object | string>;
  layout?: LayoutProps;
};

const resolveSankeyTooltipFormatter = (tooltip?: Highcharts.TooltipOptions) => {
  if (!tooltip?.formatter) return;
  if (typeof tooltip.formatter === 'function') return;

  const rawFormatter = tooltip.formatter as unknown;
  if (typeof rawFormatter === 'string') {
    const formatterKey = rawFormatter.trim();
    if (formatterKey && !formatterKey.includes('function') && !formatterKey.includes('=>')) {
      tooltip.pointFormat = tooltip.pointFormat ?? formatterKey;
      delete tooltip.formatter;
      return;
    }
  }

  delete tooltip.formatter;
  console.warn('Invalid sankey tooltip formatter');
};

const Index = ({ metadata, layout }: CustomChartProps) => {
  const theme = useTheme();
  const { isUserBasic, isUserExpired } = useUserDetails();
  const isUserExpiredValue = Boolean(isUserExpired);
  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const { aspectRatio } = getWatermarkConfig(WATERMARK_NAME);

  const seriesFileName = metadata?.seriesFileName as string;
  const configFileName = metadata?.configFileName as string;
  const queryFileId = metadata?.queryFileId as string;
  const downloadFileId = metadata?.downloadFileId as string;

  const timeRangeButtons = useMemo(
    () => (metadata?.timeRangeButtons as TimeRangeButton[]) || [],
    [metadata]
  );
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const init = useRef(false);
  const [from, setFrom] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (init.current || timeRangeButtons.length < 1) return;
    const index = timeRangeButtons.findIndex(bt => bt.default);

    if (index > -1) {
      setActiveButtonIndex(index);
      setFrom(timeRangeButtons[index].value);
      init.current = true;
    }
  }, [timeRangeButtons]);
  // Ensure context ref sync
  const { height } = layout || {};

  const {
    data: chartConfig,
    isError: isChartConfigError,
    isLoading: isChartConfigLoading
  } = useQuery(chartOptions.customConfig(configFileName, GROUP_NAME));
  const {
    data: chartSeries,
    isError: isChartSeriesError,
    isLoading: isChartSeriesLoading
  } = useQuery(
    chartOptions.customData({
      fileName: seriesFileName,
      groupName: GROUP_NAME,
      queryFileId,
      from
    })
  );
  useCsvDownload(from, downloadFileId);
  const getWatermarkAttributes = (chart: Highcharts.Chart): WatermarkAttributes => {
    const baseAttrs = getStandardWatermarkAttributes(
      chart,
      aspectRatio,
      isUserBasic,
      isUserExpiredValue
    );
    const isRestricted = isUserBasic || isUserExpiredValue;
    if (isRestricted) return baseAttrs;

    const chartSeriesOptions = Array.isArray(chart.options?.series)
      ? chart.options.series[0]
      : undefined;
    const chartType =
      chart.options?.chart?.type ??
      (chartSeriesOptions && 'type' in chartSeriesOptions ? chartSeriesOptions.type : undefined) ??
      chart.series?.[0]?.type ??
      chart.series?.[0]?.userOptions?.type;

    if (chartType === 'sankey' || chartType === 'bar' || chartType === 'column') {
      const chartWithBoxes = chart as Highcharts.Chart & {
        plotBox?: { y: number; height: number };
        spacingBox?: { y: number; height: number };
      };
      const plotBox = chartWithBoxes.plotBox ?? {
        y: chart.plotTop ?? 0,
        height: chart.plotHeight ?? 0
      };
      const spacingBox = chartWithBoxes.spacingBox ?? {
        y: 0,
        height: chart.chartHeight ?? plotBox.height
      };
      const plotBottom = plotBox.y + plotBox.height;
      const spacingBottom = spacingBox.y + spacingBox.height;
      const bottomPadding = 12;
      const belowPlotOffset = Math.max(0, spacingBottom - bottomPadding - plotBottom);
      return {
        ...baseAttrs,
        y: belowPlotOffset
      };
    }

    return baseAttrs;
  };
  const { events, exportEvents } = createEventOptions(
    () => watermarkImage,
    getWatermarkAttributes,
    WATERMARK_NAME
  );

  const { chartContainerRef } = useRegularChart({
    chartSeries
  });
  /** Build chart options */
  const resolvedChartOptions = useMemo<ExtendedOptions | null>(() => {
    if (!chartConfig || !chartSeries) return null;

    const chartHeight = height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT;

    const baseOptions: ExtendedOptions = {
      accessibility: { enabled: false },
      title: { text: undefined },
      colors: regularChartPalette,
      exporting: {
        buttons: { contextButton: { enabled: false } },
        scale: 1,
        enabled: true,
        filename: 'GX-Chart',
        chartOptions: {
          legend: {
            enabled: true,
            itemStyle: {
              color: theme.palette.text.primary,
              fontSize: '0.8em',
              textOverflow: 'ellipsis'
            }
          },
          scrollbar: { enabled: false }
        }
      },
      tooltip: { enabled: false },
      stockTools: { gui: { enabled: false } },
      legend: { enabled: true, itemStyle: { color: theme.palette.text.primary } },
      credits: { enabled: false },
      xAxis: { labels: { style: { color: theme.palette.text.primary } } },
      yAxis: {
        title: { text: '' },
        labels: { style: { color: theme.palette.text.primary } }
      },
      series: chartSeries,
      chart: {
        reflow: true,
        animation: false,
        backgroundColor: theme.palette.background.paper,
        height: chartHeight,
        style: {
          color: theme.palette.text.primary,
          fontFamily: 'Cairo, sans-serif'
        }
      }
    };

    const merged = deepMerge(baseOptions, chartConfig);

    // Handle sankey formatter safely
    if (merged.chart?.type === 'sankey') {
      resolveSankeyTooltipFormatter(merged.tooltip);
    }

    const chartType =
      merged.chart?.type ??
      (Array.isArray(merged.series) ? merged.series[0]?.type : undefined) ??
      (Array.isArray(chartConfig?.series) ? chartConfig.series[0]?.type : undefined) ??
      (Array.isArray(chartSeries) ? chartSeries[0]?.type : undefined);
    const isRestrictedUser = isUserBasic || isUserExpiredValue;
    const watermarkHeight = Math.round(100 / aspectRatio);
    const needsBottomSpace =
      !isRestrictedUser &&
      (chartType === 'bar' || chartType === 'column' || chartType === 'sankey');
    const watermarkPadding = Math.round(watermarkHeight + 12);
    const bottomSpace = chartType === 'sankey' ? watermarkPadding + 36 : watermarkPadding + 12;
    const spacingBottom = needsBottomSpace
      ? Math.max(merged.chart?.spacingBottom ?? 0, bottomSpace)
      : merged.chart?.spacingBottom;
    const chartEvents = mergeChartEvents(merged.chart?.events, events);
    const exportingChartEvents = mergeChartEvents(
      merged.exporting?.chartOptions?.chart?.events,
      exportEvents
    );

    return {
      ...merged,
      chart: {
        ...merged.chart,
        spacingBottom,
        events: chartEvents
      },
      exporting: merged.exporting
        ? {
            ...merged.exporting,
            chartOptions: {
              ...merged.exporting?.chartOptions,
              chart: {
                ...merged.exporting?.chartOptions?.chart,
                events: exportingChartEvents
              }
            }
          }
        : merged.exporting
    };
  }, [
    chartConfig,
    chartSeries,
    height,
    theme,
    events,
    exportEvents,
    aspectRatio,
    isUserBasic,
    isUserExpiredValue
  ]);

  const hasAnyData =
    Array.isArray(chartSeries) &&
    chartSeries.some(s => Array.isArray(s?.data) && s.data.length > 0);

  /** Loading & Error states */
  if (isChartSeriesLoading || isChartConfigLoading) {
    return (
      <Box sx={{ height: DEFAULT_CHART_HEIGHT }}>
        <Loader />
      </Box>
    );
  }

  /** Render chart */
  if (resolvedChartOptions) {
    return (
      <Box
        ref={chartContainerRef}
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box className='base-chart'>
          {timeRangeButtons.length > 0 && (
            <TimeRangeButtons
              activeButtonIndex={activeButtonIndex}
              setActiveButtonIndex={setActiveButtonIndex}
              timeRangeButtons={timeRangeButtons}
              onButtonClick={setFrom}
            />
          )}
          {(isChartSeriesError || isChartConfigError) && <EmptyState />}
          {hasAnyData && (
            <ChartShell
              isStockChart={false}
              options={resolvedChartOptions ?? undefined}
            />
          )}
          {!hasAnyData && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '372px',
                textAlign: 'center',
                color: 'text.secondary',
                flex: 1
              }}
            >
              <Typography variant='body1'>No data available for selected time period</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

export default Index;
